/**
 * UPT-2096 (P1-a, §4.1) — ré-encodage d'une image dans le navigateur
 * (navigateur SEULEMENT : createImageBitmap et canvas, vérifié en recette,
 * pas en jest) : lecture de l'en-tête (signature et borne de 8 000 px AVANT
 * tout décodage), décodage avec orientation EXIF, paliers de réduction
 * (2 576, 2 048, 1 600 px) jusqu'à un PNG ≤ 10 485 760 octets ; EXIF et GPS
 * retirés par le ré-encodage ; bitmap.close() en fin.
 */
import {
  fitWithin,
  pngFileName,
  PROTOTYPE_HEADER_READ_BYTES,
  PROTOTYPE_RESIZE_STEPS,
  PROTOTYPE_SOURCE_IMAGE_MAX_SIDE,
  readImageHeaderSize,
  type PreparedImage,
} from './files';
import { PROTOTYPE_LIMITS } from '../data/contracts';

export type ReencodeFailure = 'unreadable' | 'too-big-source' | 'too-heavy';

export type ReencodeResult =
  | { ok: true; image: PreparedImage }
  | { ok: false; reason: ReencodeFailure };

const readHeaderBytes = (file: Blob): Promise<Uint8Array> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(new Error('read failed'));
    reader.readAsArrayBuffer(file.slice(0, PROTOTYPE_HEADER_READ_BYTES));
  });

const toPngBlob = (bitmap: ImageBitmap, width: number, height: number): Promise<Blob | null> =>
  new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) {
      resolve(null);
      return;
    }
    context.drawImage(bitmap, 0, 0, width, height);
    canvas.toBlob((blob) => resolve(blob), 'image/png');
  });

/** Prépare un fichier image : PNG borné, nom `<base>.png`, dimensions source et cible. */
export const reencodeImage = async (file: File): Promise<ReencodeResult> => {
  let header: { format: string; width: number; height: number } | null = null;
  try {
    header = readImageHeaderSize(await readHeaderBytes(file));
  } catch {
    return { ok: false, reason: 'unreadable' };
  }
  if (!header) return { ok: false, reason: 'unreadable' };
  if (Math.max(header.width, header.height) > PROTOTYPE_SOURCE_IMAGE_MAX_SIDE) {
    return { ok: false, reason: 'too-big-source' };
  }
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  try {
    const sourceWidth = bitmap.width;
    const sourceHeight = bitmap.height;
    for (const step of PROTOTYPE_RESIZE_STEPS) {
      const target = fitWithin(sourceWidth, sourceHeight, step);
      // eslint-disable-next-line no-await-in-loop -- les paliers se tentent l'un après l'autre
      const png = await toPngBlob(bitmap, target.width, target.height);
      if (png && png.size <= PROTOTYPE_LIMITS.imageMaxBytes) {
        return {
          ok: true,
          image: {
            fileName: pngFileName(file.name),
            png,
            width: target.width,
            height: target.height,
            sourceWidth,
            sourceHeight,
          },
        };
      }
    }
    return { ok: false, reason: 'too-heavy' };
  } finally {
    bitmap.close();
  }
};
